import { yupResolver } from "@hookform/resolvers/yup";
import { FilesUploader } from "components";
import { MediaDirectoryKeys } from "constants/enums";
import {
  sectionStyleValidationSchema,
  useCreateSectionStyle,
  useSectionStyle
} from "modules/offers";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FormField, Input, MediaFilePicker, Modal } from "ui";

import {
  SectionStyleFormDataProps,
  SectionStyleModalProps
} from "./SectionStyleModal.types";

import styles from "./SectionStyleModal.module.scss";

const NEW_OFFER_FORM_ID = "SECTION_STYLE_FORM_ID";

const SectionStyleModal = ({ isOpen, onClose }: SectionStyleModalProps) => {
  const { t } = useTranslation();
  const { sectionStyle } = useSectionStyle();

  const {
    control,
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<SectionStyleFormDataProps>({
    values: {
      image: sectionStyle?.image ?? null,
      backgroundPicture: sectionStyle?.backgroundPicture ?? null,
      backgroundColor: sectionStyle?.backgroundColor ?? null
    },
    resolver: yupResolver(sectionStyleValidationSchema)
  });

  const backgroundPictureWatch = watch("backgroundPicture");

  const {
    mutate: mutateCreateSectionStyle,
    isLoading: isLoadingCreateSectionStyle
  } = useCreateSectionStyle({
    onSuccess: () => {
      onClose();
      toast.success(t("Section Style was changed successfully"));
    }
  });

  const onSubmit = (formData: SectionStyleFormDataProps) => {
    mutateCreateSectionStyle({
      imageId: formData.image?.id ?? null,
      backgroundPictureId: formData.backgroundPicture?.id ?? null,
      backgroundColor: formData.backgroundPicture?.id
        ? null
        : (formData.backgroundColor ?? null)
    });
  };

  return (
    <Modal
      formId={NEW_OFFER_FORM_ID}
      isOpen={isOpen}
      title="Section style"
      isLoading={isLoadingCreateSectionStyle}
      okText="Save"
      onClose={onClose}
      onOk={handleSubmit(onSubmit)}
      className={styles.style__modal}
    >
      <div className={styles["style__modal--content"]}>
        <Controller
          control={control}
          name="image"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormField label={"Top image"} errorMessage={error?.message}>
              <FilesUploader
                onSuccessUploading={onChange}
                uploadDirectoryKey={MediaDirectoryKeys.OfferSectionStyle}
              >
                {(uploaderProps) => (
                  <MediaFilePicker
                    {...uploaderProps}
                    value={value}
                    customSize={{ width: "full", height: 84 }}
                    onDeleteMediaFile={() => onChange(null)}
                  />
                )}
              </FilesUploader>
            </FormField>
          )}
        />

        <Controller
          control={control}
          name="backgroundPicture"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormField label={"Background image"} errorMessage={error?.message}>
              <FilesUploader
                onSuccessUploading={(media) => {
                  setValue("backgroundColor", null);
                  onChange(media);
                }}
                uploadDirectoryKey={MediaDirectoryKeys.OfferSectionStyle}
              >
                {(uploaderProps) => (
                  <MediaFilePicker
                    {...uploaderProps}
                    value={value}
                    customSize={{ width: "full", height: 166 }}
                    onDeleteMediaFile={() => onChange(null)}
                  />
                )}
              </FilesUploader>
            </FormField>
          )}
        />

        <Input
          placeholder="Ex.: #A8A8BB"
          disabled={!!backgroundPictureWatch}
          label={t("Background color")}
          {...register("backgroundColor")}
          errorMessage={errors.backgroundColor?.message}
        />
      </div>
    </Modal>
  );
};

export default SectionStyleModal;
